import { Card, Image, Text, useMediaQuery, VStack } from '@chakra-ui/react'
import React, { useEffect, useMemo, useState } from 'react'

import { useDevicesI18n } from '../i18n'
import type { DeviceModelItem } from '../types/device.types'

interface DevicePreviewCardProps {
  model: DeviceModelItem | null
}

export const DevicePreviewCard = React.memo<DevicePreviewCardProps>(
  ({ model }) => {
    const { text } = useDevicesI18n()
    const legacyModelImageName = model?.name
      ? model.name.toLowerCase().replace(/[ /]+/g, '_')
      : null

    const previewCandidates = useMemo(() => {
      const candidates: string[] = []
      if (model?.image_url) {
        candidates.push(model.image_url)
      }
      if (legacyModelImageName) {
        candidates.push(`/assets/devices/${legacyModelImageName}.jpg`)
        candidates.push(`/assets/devices/${legacyModelImageName}.png`)
      }
      candidates.push('/assets/devices/empty.jpg')
      return candidates
    }, [model?.image_url, legacyModelImageName])

    const [candidateIndex, setCandidateIndex] = useState(0)
    useEffect(() => {
      setCandidateIndex(0)
    }, [previewCandidates])

    const previewSrc =
      previewCandidates[
        Math.min(candidateIndex, Math.max(previewCandidates.length - 1, 0))
      ]
    const isEmptyFallback = previewSrc.endsWith('/empty.jpg')
    const [isNarrowLayout] = useMediaQuery(['(max-width: 640px)'])

    if (isEmptyFallback && isNarrowLayout) {
      return null
    }

    const previewAlt = model
      ? `${model.manufacturer_name} ${model.name}`
      : text('devices.preview.emptyAlt', 'Empty device preview')

    return (
      <Card.Root h="full">
        <Card.Header p={6}>
          <Card.Title>
            {text('devices.preview.title', 'Device Preview')}
          </Card.Title>
        </Card.Header>
        <Card.Body p={6} display="flex" alignItems="center">
          {isEmptyFallback ? (
            <VStack w="full" align="start" gap={2}>
              <Text color="fg.muted" fontSize="sm">
                {text(
                  'devices.preview.unavailable',
                  'Preview unavailable for selected model.',
                )}
              </Text>
            </VStack>
          ) : (
            <Image
              src={previewSrc}
              alt={previewAlt}
              onError={() =>
                setCandidateIndex((prev) =>
                  Math.min(prev + 1, previewCandidates.length - 1),
                )
              }
              borderRadius="md"
              objectFit="contain"
              w="full"
              maxH="360px"
            />
          )}
        </Card.Body>
      </Card.Root>
    )
  },
)

DevicePreviewCard.displayName = 'DevicePreviewCard'
